import { useEffect, useState } from 'react';

const VERSION_URL = `${process.env.PUBLIC_URL}/version.json`;

/** Locked for the page lifetime so UI keeps showing the running version. */
let displayVersion = null;
let displayVersionPromise = null;

const parseVersionResponse = async (response) => {
  if (!response.ok) return null;

  const data = await response.json();
  return data?.number ? data : null;
};

/**
 * Fetches version via normal request (SW StaleWhileRevalidate).
 */
export const fetchAppVersion = async () => {
  try {
    const response = await fetch(VERSION_URL);
    return await parseVersionResponse(response);
  } catch {
    return null;
  }
};

/**
 * Fetches latest version from network only (bypasses SW/HTTP cache).
 * Use for the service-worker update modal.
 */
export const fetchLatestAppVersion = async () => {
  try {
    const response = await fetch(`${VERSION_URL}?nocache=${Date.now()}`, {
      cache: 'no-store',
    });
    return await parseVersionResponse(response);
  } catch {
    return null;
  }
};

/**
 * Loads the version shown across the app (profile, login, changelog, …).
 * First successful result is locked until a full page reload (e.g. after
 * applying a service-worker update), so a pending update only appears in
 * the update modal — not in the rest of the UI.
 */
const loadDisplayVersion = () => {
  if (displayVersion) {
    return Promise.resolve(displayVersion);
  }

  if (displayVersionPromise) {
    return displayVersionPromise;
  }

  displayVersionPromise = fetchAppVersion()
    .then((data) => {
      if (data && !displayVersion) {
        displayVersion = data;
      }

      return displayVersion;
    })
    .finally(() => {
      displayVersionPromise = null;
    });

  return displayVersionPromise;
};

/**
 * Reads app version through the service worker cache strategy.
 * Does not use Redux.
 */
const useAppVersion = () => {
  const [version, setVersion] = useState(displayVersion);

  useEffect(() => {
    let isMounted = true;

    const syncVersion = async () => {
      const data = await loadDisplayVersion();
      if (isMounted && data) {
        setVersion(data);
      }
    };

    syncVersion();

    return () => {
      isMounted = false;
    };
  }, []);

  return version;
};

export default useAppVersion;
